import * as z from "zod";
import {
  BillingInvoiceSchema,
  BillingOverviewSchema,
  PlanIntervalSchema,
  SupportMessageSchema,
  SupportTicketStatusSchema,
} from "./billing-panel.js";
import { Id, IsoDate } from "./ids.js";
import {
  InferencePinOverridesSchema,
  InferenceRouteSchema,
  InferenceTierOverridesSchema,
} from "./inference-routing.js";
import { LicenseEditionSchema, PlanIdSchema } from "./plans.js";

/** Operator console contracts. Every call is audited; every mutation carries a reason. */

const Reason = z.string().trim().min(3).max(500);
const Cents = z.number().int().nonnegative();
const Ok = z.object({ ok: z.literal(true) });
const Target = z.object({ organizationId: Id, reason: Reason });

export const AdminCustomerSummarySchema = z.object({
  organizationId: Id,
  ownerUserId: Id,
  email: z.string(),
  name: z.string(),
  planId: PlanIdSchema,
  state: z.enum(["free", "trialing", "active", "past_due", "suspended", "canceled"]),
  createdAt: IsoDate,
  /** Last 30 days, BRL centavos. */
  revenueCents30d: Cents,
  costCents30d: Cents,
});
export type AdminCustomerSummary = z.infer<typeof AdminCustomerSummarySchema>;

export const AdminUsageLineSchema = z.object({
  dimension: z.enum(["task_credits", "computer_minutes", "voice_minutes"]),
  used: z.number().nonnegative(),
  limit: z.number().nonnegative().nullable(),
  unit: z.enum(["usd_micros", "seconds"]),
});

export const AdminAuditEntrySchema = z.object({
  id: Id,
  at: IsoDate,
  operatorEmail: z.string(),
  action: z.string(),
  organizationId: Id.nullable(),
  reason: z.string().nullable(),
});

export type AdminAuditEntry = z.infer<typeof AdminAuditEntrySchema>;

export const AdminCustomerDetailSchema = AdminCustomerSummarySchema.extend({
  billing: BillingOverviewSchema,
  usage: z.array(AdminUsageLineSchema),
  invoices: z.array(BillingInvoiceSchema),
  notes: z.array(z.object({ id: Id, at: IsoDate, operatorEmail: z.string(), body: z.string() })),
  audit: z.array(AdminAuditEntrySchema),
  suspended: z.boolean(),
  members: z.number().int().nonnegative(),
});
export type AdminCustomerDetail = z.infer<typeof AdminCustomerDetailSchema>;

export const AdminOverCostRowSchema = z.object({
  organizationId: Id,
  email: z.string(),
  planId: PlanIdSchema,
  revenueCents: Cents,
  costCents: Cents,
  overByCents: Cents,
});

export const AdminSupportTicketSchema = z.object({
  id: Id,
  organizationId: Id,
  customerEmail: z.string(),
  subject: z.string(),
  status: SupportTicketStatusSchema,
  createdAt: IsoDate,
  updatedAt: IsoDate,
  messages: z.array(SupportMessageSchema),
});

export const AdminLicenseSchema = z.object({
  licenseId: Id,
  licensee: z.string(),
  edition: LicenseEditionSchema,
  seats: z.number().int().positive(),
  maxAgents: z.number().int().positive(),
  features: z.array(z.string()),
  issuedAt: IsoDate,
  expiresAt: IsoDate,
  revokedAt: IsoDate.nullable(),
});

export const AdminHostSchema = z.object({
  id: Id,
  name: z.string(),
  status: z.enum(["online", "draining", "offline"]),
  computersRunning: z.number().int().nonnegative(),
  capacity: z.number().int().nonnegative().nullable(),
  lastSeenAt: IsoDate.nullable(),
});

/** US dollars per million tokens. */
export const AdminModelPriceSchema = z.object({
  input: z.number().nonnegative().max(10_000),
  output: z.number().nonnegative().max(10_000),
  cacheRead: z.number().nonnegative().max(10_000),
  cacheWrite: z.number().nonnegative().max(10_000),
});
export type AdminModelPrice = z.infer<typeof AdminModelPriceSchema>;

/** The deployment's own model connection, read from its environment and never editable here. */
export const AdminModelConnectionSchema = z.object({
  configured: z.boolean(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  /** Origin of the endpoint, without path or credentials. */
  endpoint: z.string().nullable(),
  /** The key, masked down to its last characters. */
  apiKey: z.string().nullable(),
  /** The model the operator picked in the database over the environment's, when one is set. */
  override: z.object({ provider: z.string().nullable(), model: z.string().nullable() }).nullable(),
});

/** The routing policy every workspace without its own follows. */
export const AdminRoutingPolicyInputSchema = z.object({
  residency: z
    .string()
    .trim()
    .regex(/^[a-z]{2}$/, "Residency must be a lowercase two-letter region code")
    .nullable()
    .optional(),
  allowCrossBorder: z.boolean().optional(),
  routes: z.array(InferenceRouteSchema).max(256),
  tiers: InferenceTierOverridesSchema.optional(),
  pins: InferencePinOverridesSchema.optional(),
});
export type AdminRoutingPolicyInput = z.infer<typeof AdminRoutingPolicyInputSchema>;

export const AdminModelsOverviewSchema = z.object({
  connection: AdminModelConnectionSchema,
  /** The operator's routing policy; null while no policy routes anything. */
  policy: AdminRoutingPolicyInputSchema.extend({ updatedAt: IsoDate }).nullable(),
  prices: z.array(
    z.object({
      modelId: z.string(),
      builtin: AdminModelPriceSchema.nullable(),
      override: AdminModelPriceSchema.nullable(),
      updatedBy: z.string().nullable(),
      updatedAt: IsoDate.nullable(),
    }),
  ),
});

export const AdminModelUsageSchema = z.object({
  /** Last 7 days, one line per model that served a call. */
  byModel: z.array(
    z.object({
      provider: z.string(),
      model: z.string(),
      calls: z.number().int().nonnegative(),
      inputTokens: z.number().nonnegative(),
      outputTokens: z.number().nonnegative(),
      costUsd: z.number().nonnegative(),
    }),
  ),
  /** The newest calls and the model each ran on. */
  recent: z.array(
    z.object({
      at: IsoDate,
      provider: z.string(),
      model: z.string(),
      operationKind: z.string(),
      inputTokens: z.number().nonnegative().nullable(),
      outputTokens: z.number().nonnegative().nullable(),
      costUsd: z.number().nonnegative().nullable(),
    }),
  ),
});

/** The admin section of the contract. rpc.ts mounts it at `admin/...`. */
export const adminContract = {
  customers: {
    search: {
      input: z.object({
        query: z.string().trim().max(200).default(""),
        cursor: z.string().optional(),
        limit: z.number().int().min(1).max(100).default(25),
      }),
      output: z.object({
        items: z.array(AdminCustomerSummarySchema),
        nextCursor: z.string().nullable(),
      }),
    },
    get: { input: z.object({ organizationId: Id }), output: AdminCustomerDetailSchema },
    addNote: {
      input: z.object({ organizationId: Id, body: z.string().trim().min(1).max(2000) }),
      output: Ok,
    },
  },
  compCredit: {
    input: Target.extend({ amountCents: z.number().int().positive().max(5_000_000) }),
    output: Ok,
  },
  extendTrial: { input: Target.extend({ days: z.number().int().min(1).max(60) }), output: Ok },
  changePlan: {
    input: Target.extend({
      planId: PlanIdSchema,
      interval: PlanIntervalSchema.default("monthly"),
      seats: z.number().int().positive().optional(),
    }),
    output: Ok,
  },
  refund: {
    input: Target.extend({ invoiceId: Id, amountCents: z.number().int().positive().optional() }),
    output: Ok,
  },
  cancelAtPeriodEnd: { input: Target, output: Ok },
  suspend: { input: Target, output: Ok },
  restore: { input: Target, output: Ok },
  deleteAccount: { input: Target.extend({ confirmEmail: z.string().email() }), output: Ok },
  support: {
    list: {
      input: z.object({
        status: SupportTicketStatusSchema.optional(),
        cursor: z.string().optional(),
      }),
      output: z.object({
        items: z.array(AdminSupportTicketSchema),
        nextCursor: z.string().nullable(),
      }),
    },
    get: { input: z.object({ ticketId: Id }), output: AdminSupportTicketSchema },
    reply: {
      input: z.object({
        ticketId: Id,
        body: z.string().trim().min(1).max(10_000),
        status: SupportTicketStatusSchema.optional(),
      }),
      output: AdminSupportTicketSchema,
    },
  },
  revenue: {
    input: z.object({ from: IsoDate.optional(), to: IsoDate.optional() }),
    output: z.object({
      revenueCents: Cents,
      costCents: Cents,
      marginCents: z.number().int(),
      activeSubscriptions: z.number().int().nonnegative(),
      byPlan: z.array(
        z.object({
          planId: PlanIdSchema,
          subscribers: z.number().int().nonnegative(),
          revenueCents: Cents,
          costCents: Cents,
        }),
      ),
      series: z.array(z.object({ day: z.string(), revenueCents: Cents, costCents: Cents })),
    }),
  },
  overCost: {
    input: z.object({ limit: z.number().int().min(1).max(200).default(50) }),
    output: z.array(AdminOverCostRowSchema),
  },
  hostPool: { output: z.array(AdminHostSchema) },
  /** Accounts flagged for abuse: sustained CPU with no agent work, or mail from a computer. */
  abuseFlags: {
    input: z.object({ limit: z.number().int().min(1).max(200).default(50) }),
    output: z.array(
      z.object({
        id: Id,
        organizationId: Id,
        email: z.string(),
        kind: z.enum(["mining", "smtp"]),
        detail: z.string(),
        at: IsoDate,
      }),
    ),
  },
  licenses: {
    list: { output: z.array(AdminLicenseSchema) },
    issue: {
      input: z.object({
        licensee: z.string().trim().min(1).max(200),
        edition: LicenseEditionSchema,
        seats: z.number().int().positive(),
        maxAgents: z.number().int().positive(),
        features: z.array(z.string()).default([]),
        validDays: z.number().int().min(1).max(3650).default(365),
      }),
      output: z.object({
        license: AdminLicenseSchema,
        /** The signed license file to hand to the customer. */
        file: z.string(),
      }),
    },
    revoke: { input: z.object({ licenseId: Id, reason: Reason }), output: Ok },
  },
  /** Which models serve Preá, what they cost and how traffic is routed. Hidden from customers. */
  models: {
    overview: { output: AdminModelsOverviewSchema },
    usage: { output: AdminModelUsageSchema },
    setPolicy: { input: AdminRoutingPolicyInputSchema.extend({ reason: Reason }), output: Ok },
    clearPolicy: { input: z.object({ reason: Reason }), output: Ok },
    setPrice: {
      input: AdminModelPriceSchema.extend({
        modelId: z.string().trim().min(1).max(200),
        reason: Reason,
      }),
      output: Ok,
    },
    clearPrice: {
      input: z.object({ modelId: z.string().trim().min(1).max(200), reason: Reason }),
      output: Ok,
    },
  },
  audit: {
    input: z.object({ organizationId: Id.optional(), cursor: z.string().optional() }),
    output: z.object({ items: z.array(AdminAuditEntrySchema), nextCursor: z.string().nullable() }),
  },
  /** The public launch waitlist. Every call is audited; sending the launch notice needs a reason. */
  waitlist: {
    list: {
      input: z.object({
        cursor: z.string().optional(),
        limit: z.number().int().min(1).max(200).default(50),
      }),
      output: z.object({
        total: z.number().int().nonnegative(),
        notified: z.number().int().nonnegative(),
        items: z.array(
          z.object({
            id: Id,
            email: z.string(),
            name: z.string().nullable(),
            profession: z.string().nullable(),
            source: z.string().nullable(),
            createdAt: IsoDate,
            notifiedAt: IsoDate.nullable(),
          }),
        ),
        nextCursor: z.string().nullable(),
      }),
    },
    exportCsv: { output: z.object({ csv: z.string() }) },
    notifyLaunch: {
      input: z.object({ reason: Reason }),
      output: z.object({ queued: z.boolean(), pending: z.number().int().nonnegative() }),
    },
  },
} as const;
